// PostHog client — initializes only if env vars are present. Safe no-op otherwise.
import posthog from 'posthog-js';

const KEY = process.env.REACT_APP_POSTHOG_PROJECT_API_KEY;
const HOST = process.env.REACT_APP_POSTHOG_HOST || 'https://us.i.posthog.com';

let initialized = false;

export function initPostHog() {
  if (initialized || !KEY) return null;
  try {
    posthog.init(KEY, {
      api_host: HOST,
      capture_pageview: true,
      capture_pageleave: true,
      enable_heatmaps: true,
      person_profiles: 'identified_only',
      loaded: (ph) => {
        if (process.env.NODE_ENV !== 'production') ph.debug(false);
      },
    });
    initialized = true;
    // Expose for debugging in browser DevTools
    if (typeof window !== 'undefined') window.posthog = posthog;
  } catch (e) {
    console.warn('PostHog init failed', e);
  }
  return posthog;
}

export function ph() {
  return initialized ? posthog : null;
}

export function capture(event, props = {}) {
  if (!initialized) return;
  posthog.capture(event, props);
}

export function identify(distinctId, props = {}) {
  if (!initialized) return;
  posthog.identify(distinctId, props);
}

/**
 * Privacy-conscious customer identification.
 * Hashes the email (SHA-256, hex, lowercased) so PostHog never receives the raw address.
 * Returns true if identify was attempted, false otherwise.
 */
export async function identifyCustomerByEmail(email, extraProps = {}) {
  if (!initialized || !email || typeof email !== 'string') return false;
  const clean = email.trim().toLowerCase();
  if (!clean.includes('@')) return false;
  try {
    // SHA-256 hash so raw email never leaves the device → server
    const buf = new TextEncoder().encode(clean);
    const digest = await crypto.subtle.digest('SHA-256', buf);
    const hashHex = Array.from(new Uint8Array(digest))
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('');
    const distinctId = `cust_${hashHex.slice(0, 32)}`;
    // Send only the email domain (e.g. gmail.com) as a coarse property — never the raw email
    const domain = clean.split('@')[1] || 'unknown';
    posthog.identify(distinctId, { email_domain: domain, ...extraProps });
    return true;
  } catch (_e) {
    return false;
  }
}

export function resetIdentity() {
  if (!initialized) return;
  posthog.reset();
}
